import { API_BASE } from '../config/api';

const handleResponse = async (response, fallbackMessage = 'Unable to load super admin data.') => {
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = data.error || data.message || fallbackMessage;
    throw new Error(message);
  }

  return data;
};

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

export const fetchOnlineUsers = async (token) => {
  const response = await fetch(`${API_BASE}/api/super-admin/online-users`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  return handleResponse(response, 'Unable to load online users.');
};

export const fetchPlatformSettings = async (token) => {
  const response = await fetch(`${API_BASE}/api/super-admin/platform-settings`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  return handleResponse(response, 'Unable to load platform settings.');
};

export const updatePlatformSettings = async (payload, token) => {
  const response = await fetch(`${API_BASE}/api/super-admin/platform-settings`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify(payload),
  });

  return handleResponse(response, 'Unable to update platform settings.');
};

export const fetchClubs = async ({ token, archived = false }) => {
  const response = await fetch(`${API_BASE}/api/super-admin/clubs?archived=${archived}`, {
    headers: authHeaders(token),
  });
  return handleResponse(response, 'Unable to load clubs.');
};

export const fetchOrganizations = async ({ token, archived = false }) => {
  const response = await fetch(`${API_BASE}/api/super-admin/organizations?archived=${archived}`, {
    headers: authHeaders(token),
  });
  return handleResponse(response, 'Unable to load organizations.');
};

export const fetchCommunities = async ({ token, archived = false }) => {
  const response = await fetch(`${API_BASE}/api/super-admin/communities?archived=${archived}`, {
    headers: authHeaders(token),
  });
  return handleResponse(response, 'Unable to load communities.');
};

const patchArchive = async (path, token, fallback) => {
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: authHeaders(token),
  });
  return handleResponse(response, fallback);
};

export const archiveClub = async (id, token) => patchArchive(`/api/super-admin/clubs/${id}/archive`, token, 'Unable to archive club.');
export const unarchiveClub = async (id, token) => patchArchive(`/api/super-admin/clubs/${id}/unarchive`, token, 'Unable to unarchive club.');
export const archiveOrganization = async (id, token) => patchArchive(`/api/super-admin/organizations/${id}/archive`, token, 'Unable to archive organization.');
export const unarchiveOrganization = async (id, token) => patchArchive(`/api/super-admin/organizations/${id}/unarchive`, token, 'Unable to unarchive organization.');
export const archiveCommunity = async (id, token) => patchArchive(`/api/super-admin/communities/${id}/archive`, token, 'Unable to archive community.');
export const unarchiveCommunity = async (id, token) => patchArchive(`/api/super-admin/communities/${id}/unarchive`, token, 'Unable to unarchive community.');


const buildEntityUsersQuery = ({ roles = [], q = '', page = 1, limit = 25, status = 'active' } = {}) => {
  const params = new URLSearchParams();

  if (Array.isArray(roles) && roles.length > 0) {
    params.set('roles', roles.join(','));
  }

  if (q?.trim()) {
    params.set('q', q.trim());
  }

  if (page) {
    params.set('page', String(page));
  }

  if (limit) {
    params.set('limit', String(limit));
  }

  if (status) {
    params.set('status', String(status));
  }

  return params.toString();
};

const fetchEntityUsers = async ({ token, entityType, entityId, signal, ...query }) => {
  const queryString = buildEntityUsersQuery(query);
  const path = `/api/super-admin/${entityType}/${entityId}/users`;
  const response = await fetch(`${API_BASE}${path}${queryString ? `?${queryString}` : ''}`, {
    headers: authHeaders(token),
    signal,
  });

  return handleResponse(response, 'Unable to load entity users.');
};

export const fetchOrganizationUsers = async ({ token, organizationId, ...query }) =>
  fetchEntityUsers({ token, entityType: 'organizations', entityId: organizationId, ...query });

export const fetchClubUsers = async ({ token, clubId, ...query }) =>
  fetchEntityUsers({ token, entityType: 'clubs', entityId: clubId, ...query });

export const fetchCommunityUsers = async ({ token, communityId, ...query }) =>
  fetchEntityUsers({ token, entityType: 'communities', entityId: communityId, ...query });


export const fetchSuperAdminUserProfile = async ({ token, userId, signal }) => {
  const response = await fetch(`${API_BASE}/api/superadmin/users/${userId}`, {
    headers: authHeaders(token),
    signal,
  });

  return handleResponse(response, 'Unable to load user profile.');
};

export const archiveSuperAdminUser = async ({ token, userId, archived = true }) => {
  const response = await fetch(`${API_BASE}/api/superadmin/users/${userId}/archive`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify({ archived }),
  });

  return handleResponse(response, 'Unable to update archive state.');
};

export const updateSuperAdminUserEnabledStatus = async ({ token, userId, enabled }) => {
  const response = await fetch(`${API_BASE}/api/superadmin/users/${userId}/status`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(token),
    },
    body: JSON.stringify({ enabled }),
  });

  return handleResponse(response, 'Unable to update user status.');
};
